import React, { useState, useEffect } from "react";
import { mutate } from "swr";
import useSWRMutation from "swr/mutation";
import { fetcher, delFetcher } from "api/swr";
import { cptThumb } from "commons/cdn";
import { Row } from "@tanstack/react-table";
import {
  DotsHorizontalIcon,
  TrashIcon,
  UpdateIcon,
  Link2Icon,
} from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Loader from "@/components/ui/Loader";
import { Card, CardContent } from "@/components/ui/card";

import { LayerIcon } from "./types";
export type { LayerIcon };

interface DataTableRowActionsProps {
  row: Row<LayerIcon>;
}

export const DataTableRowActions = ({ row }: DataTableRowActionsProps) => {
  const data = row.original;
  const [isComponentDialogOpen, setIsComponentDialogOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <DotsHorizontalIcon className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setIsComponentDialogOpen(true)}>
            <Link2Icon className="w-4 h-4 mr-2" />
            Cpt List
          </DropdownMenuItem>
          <DataTableRowDelete row={row} />
        </DropdownMenuContent>
      </DropdownMenu>

      <LayerIconComponentDialog
        open={isComponentDialogOpen}
        onOpenChange={setIsComponentDialogOpen}
        layerIconId={data.id}
      />
    </>
  );
};

interface LayerIconComponentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layerIconId: string;
}

const LayerIconComponentDialog = ({
  open,
  onOpenChange,
  layerIconId,
}: LayerIconComponentDialogProps) => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchComponents = async () => {
      setLoading(true);
      try {
        const matchingLayers = await fetcher([
          "/layers",
          { cover: layerIconId },
        ]);
        if (!matchingLayers || matchingLayers.length === 0) {
          setComponents([]);
          return;
        }

        const layerIds = matchingLayers.map((l) => l.id).filter(Boolean);
        const layerMap = new Map(matchingLayers.map((l) => [l.id, l]));

        const allComponents = await fetcher([
          "/components",
          { lids: layerIds },
        ]);

        const componentsWithInfo = (allComponents || []).map(
          (component: any) => {
            const layer = layerMap.get(component.lid) as any;
            return {
              ...component,
              layerId: component.lid,
              templateId: component.tid || layer?.tid,
            };
          },
        );

        setComponents(componentsWithInfo);
      } catch (error) {
        console.error("Error fetching components:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, [open, layerIconId]);

  const handleDownload = async (id: string) => {
    const url = cptThumb(id);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[80vw] w-full max-h-[80vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle>Components for {layerIconId}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader />
          </div>
        ) : components.length === 0 ? (
          <div className="text-center p-12 text-muted-foreground">
            No components found.
          </div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2 py-4">
            {components.map((component: any) => (
              <Card
                key={component.id}
                className="overflow-hidden shadow-none hover:ring-1 hover:ring-primary transition-all relative group"
              >
                <div className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-6 w-6"
                      >
                        <DotsHorizontalIcon className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel className="text-xs">
                        Component Info
                      </DropdownMenuLabel>
                      <DropdownMenuItem className="text-xs cursor-default">
                        TID: {component.templateId}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-xs cursor-default">
                        LID: {component.layerId}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDownload(component.original)}
                      >
                        Download Image
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <CardContent className="p-2">
                  <div className="aspect-square rounded-sm overflow-hidden bg-muted">
                    <img
                      src={cptThumb(component.id)}
                      alt={component.id}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const DataTableRowDelete = ({ row }: DataTableRowActionsProps) => {
  const data = row.original;
  const { toast } = useToast();
  const { trigger, isMutating } = useSWRMutation("/layericons", delFetcher, {
    onSuccess: () => {
      mutate(
        (key) => Array.isArray(key) && key[0] === "/layericons",
        undefined,
        { revalidate: true },
      );
      toast({
        title: "Layer icon Deleted",
        description: "The layer icon has been successfully deleted.",
      });
    },
  });

  const handleDelete = () => {
    trigger({ id: data.id });
  };

  const confirm = (e: Event) => {
    e.preventDefault();
    toast({
      variant: "destructive",
      title: "Delete Layer icon",
      description: "Are you sure you want to delete this layer icon?",
      action: (
        <ToastAction altText="Try again" onClick={handleDelete}>
          Delete
        </ToastAction>
      ),
    });
  };

  return (
    <DropdownMenuItem
      onSelect={confirm}
      className="text-red-500 focus:text-red-500 focus:bg-red-50"
    >
      {isMutating ? (
        <>
          <UpdateIcon className="mr-2 h-4 w-4" />
          <span>Deleteing...</span>
        </>
      ) : (
        <>
          <TrashIcon className="mr-2 h-4 w-4" />
          <span>Delete</span>
        </>
      )}
    </DropdownMenuItem>
  );
};
